import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  User,
  Calendar,
  DollarSign,
  BookOpen,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { enrollmentService } from "../../api/services/enrollment.service";
import { subscriptionService } from "../../api/services/subscription.service";
import { formatCurrency, formatDate } from "../../utils/format";

const MembershipList = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChildren, setExpandedChildren] = useState({});

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [cancellationPreview, setCancellationPreview] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState(null);
  const [subPreview, setSubPreview] = useState(null); // subscription cancellation preview
  const [cancelSuccess, setCancelSuccess] = useState(null); // success message after cancel
  const [cancelBlockedMsg, setCancelBlockedMsg] = useState(null); // blocks confirm (e.g. no active subscription)

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await enrollmentService.getMy();
      // Handle both array and {items: []} response formats
      const enrollmentsList = Array.isArray(data) ? data : data?.items || [];
      setEnrollments(enrollmentsList);

      // Auto-expand all children by default
      const childIds = [...new Set(enrollmentsList.map((e) => e.child_id))];
      const expanded = {};
      childIds.forEach((id) => {
        expanded[id] = true;
      });
      setExpandedChildren(expanded);
    } catch (err) {
      console.error("Failed to load enrollments:", err);
      setError("Failed to load memberships");
    } finally {
      setLoading(false);
    }
  };

  const toggleChild = (childId) => {
    setExpandedChildren((prev) => ({
      ...prev,
      [childId]: !prev[childId],
    }));
  };

  const handleCancelClick = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowCancelModal(true);
    setCancelReason("");
    setCancelError(null);
    setCancelSuccess(null);
    setCancelBlockedMsg(null);
    setCancellationPreview(null);
    setSubPreview(null);

    // Guard: subscription-billed membership with no Stripe subscription on record
    // (e.g. legacy enrollment paid as a one-time charge). Nothing to cancel here.
    if (
      isSubscriptionEnrollment(enrollment) &&
      !enrollment.stripe_subscription_id
    ) {
      setCancelBlockedMsg(
        "We couldn't find an active subscription for this membership. Please contact support so we can help you cancel.",
      );
      return;
    }

    // Load cancellation preview (subscription vs one-time)
    setPreviewLoading(true);
    try {
      if (isSubscriptionEnrollment(enrollment)) {
        const preview = await subscriptionService.getCancellationPreview(
          enrollment.id,
        );
        setSubPreview(preview);
      } else {
        const preview = await enrollmentService.getCancellationPreview(
          enrollment.id,
        );
        setCancellationPreview(preview);
      }
    } catch (err) {
      console.error("Failed to load cancellation preview:", err);
      setCancelError(
        "Failed to load cancellation details. You can still proceed with cancellation.",
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedEnrollment) return;

    setCancelLoading(true);
    setCancelError(null);
    try {
      let result;
      if (isSubscriptionEnrollment(selectedEnrollment)) {
        result = await subscriptionService.cancel(selectedEnrollment.id);
      } else {
        result = await enrollmentService.cancel(selectedEnrollment.id, {
          reason: cancelReason || "No reason provided",
          refund_requested: true,
        });
      }

      // Refresh enrollments list and show what happened (no more silent close)
      await loadEnrollments();
      setCancelSuccess(
        result?.message || "Your membership has been cancelled.",
      );
    } catch (err) {
      console.error("Failed to cancel enrollment:", err);
      setCancelError(
        err.response?.data?.detail ||
          "Failed to cancel membership. Please try again.",
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelectedEnrollment(null);
    setCancellationPreview(null);
    setSubPreview(null);
    setCancelReason("");
    setCancelError(null);
    setCancelSuccess(null);
    setCancelBlockedMsg(null);
  };

  // A recurring membership (vs a one-time class purchase)
  const isSubscriptionEnrollment = (e) => !!e?.is_subscription;

  // Subscription already scheduled to cancel (user-requested, not the auto
  // cancel-at-class-end date)
  const isScheduledToCancel = (e) =>
    isSubscriptionEnrollment(e) &&
    (e.cancel_at_period_end === true || !!e.subscription_cancelled_at);

  // Pausing and resuming a membership is club staff's call, not the parent's
  // (they do it from Stripe / the admin app), so this screen has no pause or
  // resume control at all. A membership staff have paused shows a plain
  // "contact us" note instead of a button that would only 403.

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toUpperCase();
    const styles = {
      ACTIVE: "bg-green-100 text-green-800 border-green-200",
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-200",
      COMPLETED: "bg-blue-100 text-blue-800 border-blue-200",
      CANCELLED: "bg-gray-100 text-gray-500 border-gray-200",
      WAITLIST: "bg-purple-100 text-purple-800 border-purple-200",
      WAITLISTED: "bg-purple-100 text-purple-800 border-purple-200",
      PAUSED: "bg-amber-100 text-amber-800 border-amber-200",
    };

    const labels = {
      ACTIVE: "Active",
      PENDING: "Pending",
      COMPLETED: "Completed",
      CANCELLED: "Cancelled",
      WAITLIST: "Waitlist",
      WAITLISTED: "Waitlist",
      PAUSED: "Paused",
    };

    return (
      <span
        className={`px-2 py-1 text-xs rounded-full border font-medium ${styles[normalizedStatus] || "bg-gray-100 text-gray-800"}`}
      >
        {labels[normalizedStatus] || status}
      </span>
    );
  };

  const canCancel = (status) => {
    const normalizedStatus = status?.toUpperCase();
    return ["ACTIVE", "PENDING", "WAITLIST", "PAUSED", "WAITLISTED"].includes(
      normalizedStatus,
    );
  };

  const isPaused = (status) => {
    return status?.toUpperCase() === "PAUSED";
  };

  // Group enrollments by child
  const groupedByChild = enrollments.reduce((acc, enrollment) => {
    const childId = enrollment.child_id;
    if (!acc[childId]) {
      // Use child_name from API response (flat string) or fallback
      const childName =
        enrollment.child_name || enrollment.child?.first_name
          ? enrollment.child_name ||
            `${enrollment.child?.first_name || ""} ${enrollment.child?.last_name || ""}`.trim()
          : "Unknown Child";

      acc[childId] = {
        childName: childName,
        enrollments: [],
      };
    }
    acc[childId].enrollments.push(enrollment);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="border rounded-xl p-5">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-xl my-3 p-5">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
        <button
          onClick={loadEnrollments}
          className="mt-3 text-sm text-btn-secondary hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (Object.keys(groupedByChild).length === 0) {
    return (
      <div className="border rounded-xl p-5">
        <h2 className="font-semibold text-lg mb-4">Active Memberships</h2>
        <div className="text-center py-8 text-gray-500">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No active memberships found</p>
          <p className="text-sm mt-1">
            Enroll your children in classes to see memberships here
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-xl p-5">
        <h2 className="font-semibold text-lg mb-4">Active Memberships</h2>

        <div className="space-y-4">
          {Object.entries(groupedByChild).map(
            ([childId, { childName, enrollments: childEnrollments }]) => (
              <div key={childId} className="border rounded-lg overflow-hidden">
                {/* Child Header */}
                <div className="flex items-center justify-between p-4 bg-gray-50">
                  <button
                    onClick={() => toggleChild(childId)}
                    className="flex items-center gap-3 flex-1 hover:bg-gray-100 -m-2 p-2 rounded-lg transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-btn-gold/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-btn-gold" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-heading-dark">
                        {childName}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {childEnrollments.length} enrollment
                        {childEnrollments.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    {expandedChildren[childId] ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 ml-auto" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 ml-auto" />
                    )}
                  </button>
                </div>

                {/* Enrollments List */}
                {expandedChildren[childId] && (
                  <div className="divide-y divide-gray-100">
                    {childEnrollments.map((enrollment) => (
                      <div
                        key={enrollment.id}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <BookOpen className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-heading-dark">
                                {enrollment.class_name ||
                                  enrollment.class?.name ||
                                  "Class"}
                              </span>
                              {getStatusBadge(enrollment.status)}
                            </div>

                            {/* Schedule Info - use weekdays from API */}
                            {enrollment.weekdays &&
                              enrollment.weekdays.length > 0 && (
                                <div className="text-sm text-gray-500 ml-6 mb-1">
                                  {enrollment.weekdays.join(", ")}
                                </div>
                              )}

                            {/* Date Range */}
                            <div className="flex items-center gap-2 text-sm text-gray-500 ml-6">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>
                                Enrolled:{" "}
                                {formatDate(
                                  enrollment.enrolled_at ||
                                    enrollment.created_at,
                                )}
                              </span>
                            </div>

                            {/* Scheduled-to-cancel notice (subscriptions) */}
                            {isScheduledToCancel(enrollment) &&
                              enrollment.subscription_cancel_at && (
                                <div className="flex items-center gap-2 text-sm text-amber-600 ml-6 mt-1">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  <span>
                                    Cancelling — active until{" "}
                                    {formatDate(
                                      enrollment.subscription_cancel_at,
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>

                          {/* Price and Actions */}
                          <div className="flex items-center gap-4 ml-6 sm:ml-0">
                            <div className="text-right">
                              <div className="flex items-center gap-1 justify-end">
                                <DollarSign className="w-4 h-4 text-gray-400" />
                                <span className="font-semibold text-heading-dark">
                                  {formatCurrency(enrollment.final_price)}
                                </span>
                              </div>
                              {enrollment.discount_amount > 0 && (
                                <div className="text-xs text-green-600">
                                  -{formatCurrency(enrollment.discount_amount)}{" "}
                                  discount
                                </div>
                              )}
                              {!enrollment.payment_completed &&
                                enrollment.status === "PENDING" && (
                                  <div className="text-xs text-yellow-600 font-medium">
                                    Payment pending
                                  </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                              {/* Paused memberships are un-paused by the club,
                                  so say who to ask rather than show a control
                                  that would be rejected. */}
                              {isPaused(enrollment.status) && (
                                <span className="px-3 py-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                                  Contact us to resume
                                </span>
                              )}

                              {/* Cancel Button */}
                              {canCancel(enrollment.status) &&
                                !isScheduledToCancel(enrollment) && (
                                  <button
                                    onClick={() =>
                                      handleCancelClick(enrollment)
                                    }
                                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
          )}
        </div>

        {/* Total Summary */}
        <div className="mt-4 pt-4 border-t space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Active Memberships</span>
            <span className="font-semibold text-heading-dark">
              {
                enrollments.filter((e) => e.status?.toUpperCase() === "ACTIVE")
                  .length
              }{" "}
              of {enrollments.length}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Monthly Cost</span>
            <span className="font-semibold text-btn-gold text-lg">
              {formatCurrency(
                enrollments
                  .filter((e) => e.status?.toUpperCase() === "ACTIVE")
                  .reduce(
                    (sum, e) => sum + (parseFloat(e.final_price) || 0),
                    0,
                  ),
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedEnrollment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg text-heading-dark">
                Cancel Membership
              </h3>
              <button
                onClick={closeCancelModal}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {cancelSuccess ? (
                /* Success state */
                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Cancellation confirmed
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {cancelSuccess}
                    </p>
                  </div>
                </div>
              ) : cancelBlockedMsg ? (
                /* Cannot self-cancel (e.g. no active subscription on record) */
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{cancelBlockedMsg}</p>
                </div>
              ) : (
                <>
                  {/* Warning */}
                  <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        Are you sure you want to cancel?
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        {isSubscriptionEnrollment(selectedEnrollment)
                          ? "Your membership will stop renewing. See the schedule below."
                          : "This action cannot be undone. The enrollment will be permanently cancelled."}
                      </p>
                    </div>
                  </div>

                  {/* Enrollment Details */}
                  <div className="border rounded-lg p-3">
                    <p className="text-sm text-gray-500">
                      Cancelling enrollment for:
                    </p>
                    <p className="font-semibold text-heading-dark mt-1">
                      {selectedEnrollment.child_name ||
                        `${selectedEnrollment.child?.first_name || ""} ${selectedEnrollment.child?.last_name || ""}`.trim() ||
                        "Child"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedEnrollment.class_name ||
                        selectedEnrollment.class?.name ||
                        "Class"}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {isSubscriptionEnrollment(selectedEnrollment)
                        ? "Monthly Price: "
                        : "Original Price: "}
                      {formatCurrency(selectedEnrollment.final_price)}
                    </p>
                  </div>

                  {/* Cancellation Preview */}
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-btn-gold" />
                      <span className="ml-2 text-gray-600">
                        Loading details...
                      </span>
                    </div>
                  ) : isSubscriptionEnrollment(selectedEnrollment) ? (
                    subPreview ? (
                      <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                        <p className="text-sm font-medium text-blue-800 mb-1">
                          What happens next
                        </p>
                        <p className="text-sm text-blue-700">
                          {subPreview.message}
                        </p>
                        <div className="flex justify-between text-sm mt-2 pt-2 border-t border-blue-200">
                          <span className="text-blue-700">Membership ends</span>
                          <span className="font-semibold text-blue-900">
                            {formatDate(subPreview.effective_end_date)}
                          </span>
                        </div>
                        {subPreview.will_charge_again && (
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-blue-700">
                              One more charge
                            </span>
                            <span className="font-medium text-blue-900">
                              {formatCurrency(subPreview.billing_amount)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null
                  ) : cancellationPreview ? (
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Refund Details
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Original Amount:
                          </span>
                          <span className="text-heading-dark">
                            {formatCurrency(cancellationPreview.refund_amount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Cancellation Fee:
                          </span>
                          <span className="text-red-600">
                            -
                            {formatCurrency(
                              cancellationPreview.cancellation_fee,
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t mt-2">
                          <span className="font-medium text-gray-700">
                            Net Refund:
                          </span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(cancellationPreview.net_refund)}
                          </span>
                        </div>
                      </div>
                      {cancellationPreview.refund_policy && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                          {cancellationPreview.refund_policy}
                        </p>
                      )}
                    </div>
                  ) : null}

                  {/* Reason Input (one-time enrollments only) */}
                  {!isSubscriptionEnrollment(selectedEnrollment) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for cancellation (optional)
                      </label>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Please let us know why you're cancelling..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-btn-gold focus:border-btn-gold resize-none"
                        rows={3}
                      />
                    </div>
                  )}

                  {/* Error Message */}
                  {cancelError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <p className="text-sm text-red-700">{cancelError}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-4 border-t bg-gray-50">
              {cancelSuccess ? (
                <button
                  onClick={closeCancelModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={closeCancelModal}
                    disabled={cancelLoading}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {cancelBlockedMsg ? "Close" : "Keep Membership"}
                  </button>
                  {!cancelBlockedMsg && (
                    <button
                      onClick={handleConfirmCancel}
                      disabled={cancelLoading}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {cancelLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        "Confirm Cancellation"
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MembershipList;
